import React, { useState } from 'react';
import { LogoIcon } from './Icons';
import NeonDivider from './NeonDivider';
import { PrivacyModal, TermsModal } from './LegalModals';

const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <footer className="relative bg-gradient-to-t from-black via-black/80 to-transparent">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* About Column */}
          <div className="mobile-padding">
            <h3 className="text-xl font-logoSans text-ivory mb-4">About Billy's WingCo</h3>
            <p className="text-gray leading-relaxed mb-4 text-responsive">
              Same legendary wings, bold new attitude. Billy's WingCo brings you 
              Scarborough's favorite flavors under neon lights with craft cocktails 
              and an electric atmosphere.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://facebook.com/billyswingco" 
                className="text-gray hover:text-neonBlue transition-colors duration-200 touch-target"
                aria-label="Visit Billy's WingCo on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://instagram.com/billyswingco" 
                className="text-gray hover:text-neonBlue transition-colors duration-200"
                aria-label="Visit Billy's WingCo on Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 1.804c3.15 0 3.523.014 4.765.069 1.15.054 1.771.24 2.185.398a3.634 3.634 0 011.342.873 3.634 3.634 0 01.873 1.342c.158.414.344 1.035.398 2.185.054 1.242.069 1.615.069 4.765s-.014 3.523-.069 4.765c-.054 1.15-.24 1.771-.398 2.185a3.634 3.634 0 01-.873 1.342 3.634 3.634 0 01-1.342.873c-.414.158-1.035.344-2.185.398-1.242.054-1.615.069-4.765.069s-3.523-.014-4.765-.069c-1.15-.054-1.771-.24-2.185-.398a3.634 3.634 0 01-1.342-.873 3.634 3.634 0 01-.873-1.342c-.158-.414-.344-1.035-.398-2.185C1.818 15.54 1.804 15.167 1.804 12.017s.014-3.523.069-4.765c.054-1.15.24-1.771.398-2.185a3.634 3.634 0 01.873-1.342A3.634 3.634 0 014.486 2.85c.414-.158 1.035-.344 2.185-.398 1.242-.054 1.615-.069 4.765-.069zm0-1.804C8.815 0 8.4.014 7.128.069 5.862.125 4.985.325 4.223.615a5.438 5.438 0 00-1.963 1.28A5.438 5.438 0 00.98 3.858c-.29.762-.49 1.639-.546 2.905C.379 8.035.365 8.45.365 12.017s.014 3.982.069 5.254c.056 1.266.256 2.143.546 2.905a5.438 5.438 0 001.28 1.963 5.438 5.438 0 001.963 1.28c.762.29 1.639.49 2.905.546 1.272.055 1.687.069 5.254.069s3.982-.014 5.254-.069c1.266-.056 2.143-.256 2.905-.546a5.438 5.438 0 001.963-1.28 5.438 5.438 0 001.28-1.963c.29-.762.49-1.639.546-2.905.055-1.272.069-1.687.069-5.254s-.014-3.982-.069-5.254c-.056-1.266-.256-2.143-.546-2.905a5.438 5.438 0 00-1.28-1.963A5.438 5.438 0 0019.895.615c-.762-.29-1.639-.49-2.905-.546C15.618.014 15.203 0 12.017 0z"/>
                  <path d="M12.017 5.838a6.179 6.179 0 100 12.358 6.179 6.179 0 000-12.358zm0 10.188a4.009 4.009 0 110-8.018 4.009 4.009 0 010 8.018z"/>
                  <circle cx="18.406" cy="5.594" r="1.44"/>
                </svg>
              </a>
              <a 
                href="https://x.com/billyswingco" 
                className="text-gray hover:text-neonBlue transition-colors duration-200"
                aria-label="Visit Billy's WingCo on X (Twitter)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-xl font-logoSans text-ivory mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a href="#menu" className="text-gray hover:text-neonBlue transition-colors duration-200">
                  View Menu
                </a>
              </li>
              <li>
                <a href="#order" className="text-gray hover:text-neonBlue transition-colors duration-200">
                  Order Online
                </a>
              </li>
              <li>
                <a href="#catering" className="text-gray hover:text-neonBlue transition-colors duration-200">
                  Catering
                </a>
              </li>
              <li>
                <a href="#events" className="text-gray hover:text-neonBlue transition-colors duration-200">
                  Private Events
                </a>
              </li>
              <li>
                <a href="#careers" className="text-gray hover:text-neonBlue transition-colors duration-200">
                  Careers
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-xl font-logoSans text-ivory mb-4">Visit Us</h3>
            <div className="space-y-4">
              <div className="p-4 border border-neonRed/20 rounded-lg bg-black/30">
                <h4 className="font-semibold text-neonRed mb-2">Location & Hours</h4>
                <p className="text-gray text-sm leading-relaxed">
                  3490 Kingston Rd<br/>
                  Scarborough, ON M1M 1R5<br/>
                  <span className="text-neonRed font-medium">(416) 265-9223</span>
                </p>
                <div className="mt-3 text-sm text-gray">
                  <p>Sun–Tue 12 PM – 1 AM</p>
                  <p>Wed–Sat 12 PM – 2 AM</p>
                  <p className="text-xs mt-2 text-ivory italic font-normal" style={{fontVariant: 'small-caps'}}>Pickup closes 1 hour before dining-room close</p>
                </div>
                <a
                  href="https://maps.app.goo.gl/X8SdCHyZtU65jmyF8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-3 text-neonRed hover:text-ivory transition-colors duration-200 text-sm font-medium"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Directions
                </a>
              </div>
              
              {/* Google Mini-Map */}
              <div className="h-32 border border-gray/20 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1440.6!2d-79.284821!3d43.763264!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f18.0!3m3!1m2!1s0x89d4d1a05b9f7b%3A0x12345678!2s3490%20Kingston%20Rd%2C%20Scarborough%2C%20ON%20M1M%201R5!5e0!3m2!1sen!2sca!4v1640995200000!5m2!1sen!2sca"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Billy's WingCo Location"
                  className="filter grayscale hover:grayscale-0 transition-all duration-300"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        <NeonDivider color="neonBlue" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <LogoIcon className="h-8 opacity-80" />
            <span className="text-gray text-sm">
              © 2025 Billy's WingCo. All rights reserved.
            </span>
          </div>
          
          <div className="flex gap-6 text-sm">
            <button 
              onClick={() => setShowPrivacy(true)}
              className="text-gray hover:text-neonBlue transition-colors duration-200"
            >
              Privacy Policy
            </button>
            <span className="text-gray">|</span>
            <button 
              onClick={() => setShowTerms(true)}
              className="text-gray hover:text-neonBlue transition-colors duration-200"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </footer>
  );
};

export default Footer; 